import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { showLocalNotification } from '@/lib/notifications';
import { useToastStore, type Toast } from '@/stores/toast';
import type { Notification } from '@trackkm/shared';

const POLL_INTERVAL_MS = 30_000;
const LAST_SEEN_KEY = 'trackkm-last-seen-notification';

const NOTIFICATION_TYPE_MAP: Record<string, Toast['type']> = {
  trip_approved: 'success',
  dispute_resolved: 'success',
  trip_rejected: 'error',
  trip_disputed: 'warning',
  trip_submitted: 'info',
  pin_changed: 'info',
  system: 'info',
};

function getLastSeenId(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

function setLastSeenId(id: string): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, id);
  } catch {
    // localStorage unavailable — ignore
  }
}

/**
 * Polls the notifications endpoint every 30 seconds.
 * Shows a browser/native notification for each new unread item since the last seen ID.
 * Returns the current unread count for badge display.
 */
export function useNotificationPoller(): { readonly unreadCount: number } {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const lastSeenRef = useRef<string | null>(getLastSeenId());

  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const result = await api.get<Notification[]>('/notifications');
      return result.data ?? [];
    },
    enabled: isAuthenticated,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  const addToast = useToastStore((s) => s.addToast);

  const handleNewNotifications = useCallback((items: readonly Notification[]) => {
    const lastSeenId = lastSeenRef.current;

    // Find notifications newer than the last seen one
    const newItems: readonly Notification[] = lastSeenId
      ? items.filter((n) => !n.read && n.id > lastSeenId)
      : []; // On first load, don't spam — just record the latest

    // Show in-app toast + browser notification for each new item
    for (const item of newItems) {
      addToast({
        title: item.title,
        message: item.message,
        type: NOTIFICATION_TYPE_MAP[item.type] ?? 'info',
      });
      showLocalNotification(item.title, item.message, () => {
        window.location.href = '/notifications';
      });
    }

    // Update last seen to the newest notification id
    const newest = items[0];
    if (newest) {
      lastSeenRef.current = newest.id;
      setLastSeenId(newest.id);
    }
  }, [addToast]);

  useEffect(() => {
    if (notifications && notifications.length > 0) {
      handleNewNotifications(notifications);
    }
  }, [notifications, handleNewNotifications]);

  const unreadCount = notifications
    ? notifications.filter((n) => !n.read).length
    : 0;

  return { unreadCount };
}
